import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import Breadcrumbs from '../components/Breadcrumbs'

type Org = { id: number; name: string }
type TicketRow = { id: number; organization_id: number; organization_name: string; subject: string; status: string; updated_at: string }

export default function Admin() {
  const { t } = useTranslation()
  const [orgs, setOrgs] = useState<Org[]>([])
  const [tickets, setTickets] = useState<TicketRow[]>([])
  const [loading, setLoading] = useState(true)

  const [orgName, setOrgName] = useState('')
  const [inviteOrgId, setInviteOrgId] = useState<number | ''>('')
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteName, setInviteName] = useState('')

  const [contractOrgId, setContractOrgId] = useState<number | ''>('')
  const [contractTitle, setContractTitle] = useState('')
  const [contractStart, setContractStart] = useState('')
  const [contractEnd, setContractEnd] = useState('')
  const [contractStatus, setContractStatus] = useState('active')
  const [contractNotes, setContractNotes] = useState('')

  const refresh = async () => {
    const [o, tk] = await Promise.all([
      axios.get('/api/admin/organizations'),
      axios.get('/api/admin/tickets'),
    ])
    setOrgs(o.data?.organizations ?? [])
    setTickets(tk.data?.tickets ?? [])
  }

  useEffect(() => {
    let mounted = true
    ;(async () => {
      try {
        await refresh()
      } finally {
        if (mounted) setLoading(false)
      }
    })()
    return () => {
      mounted = false
    }
  }, [])

  const createOrg = async (e: React.FormEvent) => {
    e.preventDefault()
    await axios.post('/api/admin/organizations', { name: orgName })
    setOrgName('')
    await refresh()
  }

  const sendInvite = async (e: React.FormEvent) => {
    e.preventDefault()
    if (inviteOrgId === '') return
    await axios.post('/api/admin/invite', {
      organizationId: inviteOrgId,
      email: inviteEmail,
      name: inviteName || null,
    })
    setInviteEmail('')
    setInviteName('')
  }

  const createContract = async (e: React.FormEvent) => {
    e.preventDefault()
    if (contractOrgId === '') return
    await axios.post('/api/admin/contracts', {
      organizationId: contractOrgId,
      title: contractTitle,
      startDate: contractStart || null,
      endDate: contractEnd || null,
      status: contractStatus || 'active',
      notes: contractNotes || null,
    })
    setContractTitle('')
    setContractStart('')
    setContractEnd('')
    setContractNotes('')
    setContractStatus('active')
  }

  const orgOptions = useMemo(() => orgs.map((o) => ({ value: o.id, label: o.name })), [orgs])

  return (
    <div className="min-h-screen pb-24">
      <Breadcrumbs items={[{ key: 'admin' }]} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl sm:text-4xl font-bold mb-8">{t('admin.title')}</h1>

        {loading ? (
          <div className="bg-slate-200 dark:bg-slate-700 animate-pulse rounded-2xl h-64" />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Orgs */}
            <section className="bg-white dark:bg-slate-800 rounded-2xl p-6 border border-slate-200 dark:border-slate-700 shadow-sm">
              <h2 className="text-xl font-bold mb-4">{t('admin.organizations')}</h2>
              <ul className="space-y-2 mb-6">
                {orgs.map((o) => (
                  <li key={o.id} className="flex items-center justify-between p-3 rounded-xl bg-slate-50 dark:bg-slate-900/40 border border-slate-100 dark:border-slate-700">
                    <span className="font-medium">{o.name}</span>
                    <span className="text-xs text-slate-500">#{o.id}</span>
                  </li>
                ))}
              </ul>

              <form onSubmit={createOrg} className="space-y-3">
                <p className="font-semibold">{t('admin.createOrg')}</p>
                <input
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  required
                  placeholder={t('admin.orgName')}
                  className="w-full px-4 py-3 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700"
                />
                <button className="w-full px-6 py-3 bg-primary text-white rounded-xl font-semibold hover:bg-primary/90 transition-colors">
                  {t('admin.create')}
                </button>
              </form>
            </section>

            {/* Invite + Contracts */}
            <section className="bg-white dark:bg-slate-800 rounded-2xl p-6 border border-slate-200 dark:border-slate-700 shadow-sm space-y-8">
              <div>
                <h2 className="text-xl font-bold mb-4">{t('admin.invite')}</h2>
                <form onSubmit={sendInvite} className="space-y-3">
                  <select
                    value={inviteOrgId}
                    onChange={(e) => setInviteOrgId(e.target.value ? Number(e.target.value) : '')}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700"
                  >
                    <option value="">{t('admin.inviteOrg')}</option>
                    {orgOptions.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <input
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    required
                    placeholder={t('admin.inviteEmail')}
                    className="w-full px-4 py-3 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700"
                  />
                  <input
                    value={inviteName}
                    onChange={(e) => setInviteName(e.target.value)}
                    placeholder={t('admin.inviteName')}
                    className="w-full px-4 py-3 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700"
                  />
                  <button className="w-full px-6 py-3 bg-primary text-white rounded-xl font-semibold hover:bg-primary/90 transition-colors">
                    {t('admin.sendInvite')}
                  </button>
                </form>
              </div>

              <div>
                <h2 className="text-xl font-bold mb-4">{t('admin.createContract')}</h2>
                <form onSubmit={createContract} className="space-y-3">
                  <select
                    value={contractOrgId}
                    onChange={(e) => setContractOrgId(e.target.value ? Number(e.target.value) : '')}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700"
                  >
                    <option value="">{t('admin.inviteOrg')}</option>
                    {orgOptions.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <input
                    value={contractTitle}
                    onChange={(e) => setContractTitle(e.target.value)}
                    required
                    placeholder={t('admin.contractTitle')}
                    className="w-full px-4 py-3 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700"
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input
                      type="date"
                      value={contractStart}
                      onChange={(e) => setContractStart(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700"
                    />
                    <input
                      type="date"
                      value={contractEnd}
                      onChange={(e) => setContractEnd(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700"
                    />
                  </div>
                  <input
                    value={contractStatus}
                    onChange={(e) => setContractStatus(e.target.value)}
                    placeholder={t('admin.status')}
                    className="w-full px-4 py-3 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700"
                  />
                  <textarea
                    value={contractNotes}
                    onChange={(e) => setContractNotes(e.target.value)}
                    rows={3}
                    placeholder={t('admin.notes')}
                    className="w-full px-4 py-3 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 resize-none"
                  />
                  <button className="w-full px-6 py-3 bg-primary text-white rounded-xl font-semibold hover:bg-primary/90 transition-colors">
                    {t('admin.create')}
                  </button>
                </form>
              </div>
            </section>

            {/* Ticket list */}
            <section className="lg:col-span-2 bg-white dark:bg-slate-800 rounded-2xl p-6 border border-slate-200 dark:border-slate-700 shadow-sm">
              <h2 className="text-xl font-bold mb-4">{t('admin.tickets')}</h2>
              <div className="overflow-auto">
                <table className="min-w-full text-sm">
                  <thead className="text-left text-slate-500">
                    <tr>
                      <th className="py-2 pr-4">#</th>
                      <th className="py-2 pr-4">{t('admin.inviteOrg')}</th>
                      <th className="py-2 pr-4">{t('portal.subject')}</th>
                      <th className="py-2 pr-4">{t('admin.status')}</th>
                      <th className="py-2 pr-4">Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tickets.map((tk) => (
                      <tr key={tk.id} className="border-t border-slate-100 dark:border-slate-700">
                        <td className="py-2 pr-4 font-semibold">#{tk.id}</td>
                        <td className="py-2 pr-4">{tk.organization_name}</td>
                        <td className="py-2 pr-4">{tk.subject}</td>
                        <td className="py-2 pr-4">{tk.status}</td>
                        <td className="py-2 pr-4 text-slate-500">{new Date(tk.updated_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  )
}

